// oxlint-disable typescript/no-explicit-any
import { isEqual, isArray, isObject } from 'lodash-es';
import { useRef, useMemo } from 'react';
/**
 * Stabilizes an object by recursively comparing the keys and values of the object,
 * changing the references of each nested object only if it has changed.
 */
export function stabilize(next, curr) {
    if (!curr)
        return next;
    // If objects are deeply equal, return the original (curr)
    if (isEqual(curr, next))
        return curr;
    if (typeof curr !== 'object' || curr === null || typeof next !== 'object' || next === null) {
        return next;
    }
    const result = isArray(curr) ? [] : {};
    let changed = false;
    // Combine keys from both objects
    const keys = new Set([...Object.keys(curr), ...Object.keys(next)]);
    keys.forEach((key) => {
        if (isEqual(curr[key], next[key])) {
            result[key] = curr[key];
        }
        else if (isObject(curr[key]) && isObject(next[key])) {
            // Recursively stabilize nested objects/arrays
            const value = stabilize(next[key], curr[key]);
            if (!isEqual(value, curr[key])) {
                changed = true;
                result[key] = value;
            }
            else {
                result[key] = curr[key];
            }
        }
        else {
            changed = true;
            if (key in next)
                result[key] = next[key];
        }
    });
    // If no changes, return the original
    return changed ? result : curr;
}
/**
 * Hook that stabilizes an object by recursively comparing the keys and values of the object,
 * changing the references of each nested object only if it has changed.
 */
export function useStabilized(object) {
    const ref = useRef(object);
    return useMemo(() => {
        ref.current = stabilize(object, ref.current);
        return ref.current;
    }, [object]);
}
//# sourceMappingURL=useStabilized.js.map